import { useCallback, useEffect, useState } from "react";
import { useNavigationType, useSearchParams } from "react-router-dom";
import apiFetch from "../utils/apiFetch";

export function useStatisticsQuery(defaults = {}) {
  const [params, setParams] = useSearchParams();
  const values = { ...defaults, ...Object.fromEntries(params.entries()) };
  const update = (changes, { resetPage = true } = {}) => {
    setParams((previous) => {
      const next = new URLSearchParams(previous);
      if (resetPage) next.delete("page");
      Object.entries(changes).forEach(([key, value]) => {
        if (value === "" || value === null || value === undefined) next.delete(key);
        else next.set(key, String(value));
      });
      return next;
    }, { replace: Object.prototype.hasOwnProperty.call(changes, "q") });
  };
  return { values, update, reset: () => setParams({}) };
}

export function statisticsSearch(values, keys) {
  const params = new URLSearchParams();
  keys.forEach((key) => {
    if (values[key] !== "" && values[key] !== undefined && values[key] !== null) params.set(key, String(values[key]));
  });
  return params.toString();
}

export function useStatisticsSearch(values, keys) {
  const navigationType = useNavigationType();
  const query = statisticsSearch(values, keys);
  const q = values.q || "";
  const [settled, setSettled] = useState({ query, q });
  // Search edits replace the URL and may also reset page/owner filters. Delay
  // the entire query together; other controls and browser history apply now.
  const waitingForSearch = navigationType === "REPLACE" && q !== settled.q;
  useEffect(() => {
    const settle = () => setSettled((previous) => previous.query === query && previous.q === q ? previous : { query, q });
    if (!waitingForSearch) {
      settle();
      return undefined;
    }
    const timer = setTimeout(settle, 300);
    return () => clearTimeout(timer);
  }, [query, q, waitingForSearch]);
  return waitingForSearch ? settled.query : query;
}

export function useStatisticsResource(path) {
  const [state, setState] = useState({ data: null, loading: true, error: "" });
  const [revision, setRevision] = useState(0);
  const retry = useCallback(() => setRevision((value) => value + 1), []);
  useEffect(() => {
    const controller = new AbortController();
    setState({ data: null, loading: true, error: "" });
    if (!path) {
      setState({ data: null, loading: false, error: "" });
      return () => controller.abort();
    }
    (async () => {
      try {
        const response = await apiFetch(path, { signal: controller.signal, cache: "no-store" });
        if (!response.ok) throw new Error("REQUEST_FAILED");
        const data = await response.json();
        if (!controller.signal.aborted) setState({ data, loading: false, error: "" });
      } catch (error) {
        if (!controller.signal.aborted) setState({
          data: null,
          loading: false,
          error: error.status === 401 ? "Сессия якунланган. Қайта киринг." : error.status === 403 ? "Бу маълумотларни кўришга рухсат йўқ." : "Маълумотлар юкланмади. Қайта уриниб кўринг.",
        });
      }
    })();
    return () => controller.abort();
  }, [path, revision]);
  return { ...state, retry };
}

export function tashkentDate(value = new Date()) {
  const parts = new Intl.DateTimeFormat("en-CA", { timeZone: "Asia/Tashkent", year: "numeric", month: "2-digit", day: "2-digit" }).formatToParts(value);
  const get = (key) => parts.find((part) => part.type === key)?.value;
  return `${get("year")}-${get("month")}-${get("day")}`;
}

export function datePreset(preset) {
  const to = tashkentDate();
  if (preset === "month") return { from: `${to.slice(0, 7)}-01`, to };
  const days = preset === "today" ? 1 : Number(preset);
  if (!Number.isInteger(days) || days < 1) throw new RangeError("Invalid statistics date preset");
  const start = new Date(`${to}T12:00:00+05:00`);
  start.setUTCDate(start.getUTCDate() - (days - 1));
  return { from: tashkentDate(start), to };
}

